<script setup lang="ts">
/**
 * Horizontal bars: one row per category — label, bar, value — for rankings
 * and long category names. Several series stack within the row; negative
 * values grow left from a shared zero line, on the same scale as the rest.
 *
 * The longest bar fills the track (no value axis: every row carries its value
 * at the end). Hovering a row of a stacked chart shows each series' part in a
 * tooltip; two or more series get a legend. Rows grow with the content — put
 * the chart in a scrolling box for long lists.
 */
import { computed, inject, ref, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import { announceTooltip } from './announce';
import NxChartLegend from './NxChartLegend.vue';
import NxChartLive from './NxChartLive.vue';
import NxChartTable from './NxChartTable.vue';
import NxChartTooltip, { type NxChartTooltipRow } from './NxChartTooltip.vue';
import {
  chartColor,
  chartValue,
  stackColumns,
  type NxChartKey,
  type NxChartSeries,
} from './series';

const props = withDefaults(
  defineProps<{
    categories: readonly string[];
    series: readonly NxChartSeries[];
    ariaLabel?: string;
    legend?: 'auto' | 'none';
    formatValue?: (value: number) => string;
    totalLabel?: string;
    locale?: string;
    /** Spoken hint for keyboard users, appended to the rows' name. */
    keyboardHint?: string;
  }>(),
  {
    ariaLabel: undefined,
    legend: 'auto',
    formatValue: undefined,
    totalLabel: 'Summe',
    locale: undefined,
    keyboardHint: 'Pfeiltasten wechseln die Zeile',
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const numberFormat = computed(() => new Intl.NumberFormat(locale.value));
function format(value: number): string {
  return props.formatValue ? props.formatValue(value) : numberFormat.value.format(value);
}

const stack = computed(() => stackColumns(props.categories, props.series));
const span = computed(() => stack.value.max - stack.value.min || 1);
/** Where zero sits on the track, 0–100 %. */
const zeroPct = computed(() => (-stack.value.min / span.value) * 100);
const hasNegative = computed(() => stack.value.min < 0);

const rows = computed(() =>
  stack.value.columns.map((col) => {
    const pos = col.segments.filter((s) => s.value > 0);
    const neg = col.segments.filter((s) => s.value < 0);
    const segs = [pos, neg].flatMap((group) =>
      group.map((s, i) => {
        const lo = Math.min(s.from, s.to);
        const hi = Math.max(s.from, s.to);
        const outer = i === group.length - 1;
        return {
          key: s.seriesIndex,
          seriesKey: s.key,
          left: ((lo - stack.value.min) / span.value) * 100,
          width: ((hi - lo) / span.value) * 100,
          color: s.color,
          negative: s.value < 0,
          /** Inner joints get the surface gap; the outer end is rounded. */
          inner: i > 0,
          outer,
        };
      })
    );
    return { index: col.index, label: col.label, segs, net: col.net, hasValue: col.hasValue };
  })
);

const highlightedSeries = ref<NxChartKey | null>(null);
const active = ref<number | null>(null);
const listRef = useTemplateRef<HTMLElement>('list');
const anchor = ref({ x: 0, y: 0, w: 0 });

function enter(index: number, event: PointerEvent | FocusEvent) {
  active.value = index;
  const list = listRef.value?.getBoundingClientRect();
  const row = (event.currentTarget as HTMLElement).getBoundingClientRect();
  if (!list) return;
  const x =
    event instanceof PointerEvent
      ? event.clientX - list.left
      : row.left - list.left + row.width / 2;
  anchor.value = { x, y: row.top - list.top + row.height / 2, w: list.width };
}

const tooltip = computed(() => {
  const i = active.value;
  if (i === null || props.series.length < 2) return null;
  const col = stack.value.columns[i];
  if (!col) return null;
  const tipRows: NxChartTooltipRow[] = props.series.map((s, si) => {
    const v = chartValue(s.values[i]);
    return {
      key: s.key,
      label: s.label,
      value: v === null ? '–' : format(v),
      color: chartColor(s.color, si),
      muted: v === null,
    };
  });
  return {
    title: col.label,
    rows: tipRows,
    footer: col.hasValue ? `${props.totalLabel} ${format(col.net)}` : undefined,
  };
});

/** Tooltip anchor at the middle of row `index` (keyboard). */
function anchorRow(index: number) {
  const list = listRef.value;
  const row = list?.children[index] as HTMLElement | undefined;
  if (!list || !row) return;
  const box = list.getBoundingClientRect();
  const r = row.getBoundingClientRect();
  anchor.value = {
    x: r.left - box.left + r.width / 2,
    y: r.top - box.top + r.height / 2,
    w: box.width,
  };
}

function onKeydown(event: KeyboardEvent) {
  const n = rows.value.length;
  if (!n) return;
  const cur = active.value;
  let next: number;
  switch (event.key) {
    case 'ArrowDown':
    case 'ArrowRight':
      next = cur === null ? 0 : Math.min(cur + 1, n - 1);
      break;
    case 'ArrowUp':
    case 'ArrowLeft':
      next = cur === null ? n - 1 : Math.max(cur - 1, 0);
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = n - 1;
      break;
    case 'Escape':
      active.value = null;
      return;
    default:
      return;
  }
  event.preventDefault();
  active.value = next;
  anchorRow(next);
}

/** What the live region says: only while the rows have keyboard focus. */
const focused = ref(false);
const announcement = computed(() => {
  if (!focused.value || active.value === null) return '';
  if (tooltip.value) return announceTooltip(tooltip.value);
  const row = rows.value[active.value];
  return row ? `${row.label}: ${row.hasValue ? format(row.net) : '–'}` : '';
});
const rowsLabel = computed(() => [props.ariaLabel, props.keyboardHint].filter(Boolean).join('. '));

const legendItems = computed(() =>
  props.series.map((s, i) => ({ key: s.key, label: s.label, color: chartColor(s.color, i) }))
);
const showLegend = computed(() => props.legend === 'auto' && props.series.length > 1);

const table = computed(() => ({
  columns: ['', ...props.series.map((s) => s.label)],
  rows: props.categories.map((c, i) => ({
    key: i,
    cells: [
      c,
      ...props.series.map((s) => {
        const v = chartValue(s.values[i]);
        return v === null ? '–' : format(v);
      }),
    ],
  })),
}));

function segStyle(seg: (typeof rows.value)[number]['segs'][number]) {
  const gapStart = seg.negative ? (seg.outer ? 0 : 1) : seg.inner ? 1 : 0;
  const gapEnd = seg.negative ? (seg.inner ? 1 : 0) : seg.outer ? 0 : 1;
  return {
    left: `calc(${seg.left}% + ${gapStart}px)`,
    width: `max(1px, calc(${seg.width}% - ${gapStart + gapEnd}px))`,
    background: seg.color,
  };
}
</script>

<template>
  <div class="nx-hbar-chart" role="group" :aria-label="props.ariaLabel">
    <div
      class="nx-hbar-chart__rows"
      tabindex="0"
      role="group"
      :aria-label="rowsLabel"
      @keydown="onKeydown"
      @focus="focused = true"
      @blur="
        focused = false;
        active = null;
      "
    >
      <ul ref="list" class="nx-hbar-chart__list" role="list" aria-hidden="true">
        <li
          v-for="row in rows"
          :key="row.index"
          class="nx-hbar-chart__row"
          :class="{ 'is-active': active === row.index }"
          @pointerenter="enter(row.index, $event)"
          @pointermove="enter(row.index, $event)"
          @pointerleave="active = null"
        >
          <span class="nx-hbar-chart__label" :title="row.label">{{ row.label }}</span>
          <span class="nx-hbar-chart__track">
            <span v-if="hasNegative" class="nx-hbar-chart__zero" :style="{ left: `${zeroPct}%` }" />
            <span
              v-for="seg in row.segs"
              :key="seg.key"
              class="nx-hbar-chart__fill"
              :class="{
                'is-negative': seg.negative,
                'is-outer': seg.outer,
                'is-dimmed': highlightedSeries !== null && highlightedSeries !== seg.seriesKey,
              }"
              :style="segStyle(seg)"
            />
          </span>
          <span class="nx-hbar-chart__value">{{ row.hasValue ? format(row.net) : '–' }}</span>
        </li>
      </ul>
      <NxChartLive :text="announcement" />
    </div>
    <NxChartTooltip
      v-if="tooltip"
      :x="anchor.x"
      :y="anchor.y"
      :box-width="anchor.w"
      :title="tooltip.title"
      :rows="tooltip.rows"
      :footer="tooltip.footer"
    />
    <NxChartLegend v-if="showLegend" v-model:highlighted="highlightedSeries" :items="legendItems" />
    <NxChartTable :caption="props.ariaLabel" :columns="table.columns" :rows="table.rows" />
  </div>
</template>

<style scoped>
.nx-hbar-chart {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  width: 100%;
  min-width: 0;
}
.nx-hbar-chart__rows {
  position: relative;
  border-radius: var(--nxt-radius-sm);
  outline: none;
}
.nx-hbar-chart__rows:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-hbar-chart__list {
  display: grid;
  grid-template-columns: minmax(calc(var(--nxt-size-unit) * 3), max-content) minmax(0, 1fr) auto;
  gap: var(--nxt-space-2xs) var(--nxt-space-sm);
  margin: 0;
  padding: 0;
  list-style: none;
}
/* A real box per row (hover wash, tooltip anchor) that still shares the list's
   three columns, so labels, tracks and values line up across rows. */
.nx-hbar-chart__row {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: center;
  margin-inline: calc(var(--nxt-space-xs) * -1);
  padding-inline: var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-hbar-chart__row.is-active {
  background: var(--nxt-chart-grid);
}
.nx-hbar-chart__row > * {
  transition:
    opacity var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-hbar-chart__label {
  max-width: calc(var(--nxt-size-unit) * 12);
  overflow: hidden;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-control-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-hbar-chart__row.is-active .nx-hbar-chart__label,
.nx-hbar-chart__row.is-active .nx-hbar-chart__value {
  color: var(--nxt-fg);
}
.nx-hbar-chart__track {
  position: relative;
  align-self: center;
  height: calc(var(--nxt-size-unit) * 0.75);
  border-radius: calc(var(--nxt-hairline) * 4);
  background: var(--nxt-chart-track);
}
.nx-hbar-chart__zero {
  position: absolute;
  inset-block: calc(var(--nxt-hairline) * -3);
  width: var(--nxt-hairline);
  background: var(--nxt-chart-baseline);
}
.nx-hbar-chart__fill {
  position: absolute;
  inset-block: 0;
  transform-origin: left center;
  animation: nx-hbar-grow var(--nxt-motion-slow) var(--nxt-ease-out) both;
}
.nx-hbar-chart__fill.is-negative {
  transform-origin: right center;
}
.nx-hbar-chart__fill.is-outer {
  border-start-end-radius: calc(var(--nxt-hairline) * 4);
  border-end-end-radius: calc(var(--nxt-hairline) * 4);
}
.nx-hbar-chart__fill.is-outer.is-negative {
  border-radius: calc(var(--nxt-hairline) * 4) 0 0 calc(var(--nxt-hairline) * 4);
}
.nx-hbar-chart__fill.is-dimmed {
  opacity: 0.3;
}
.nx-hbar-chart__row.is-active .nx-hbar-chart__fill {
  filter: brightness(1.08) saturate(1.1);
}
.nx-hbar-chart__value {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  line-height: var(--nxt-control-size-xs);
  text-align: end;
  white-space: nowrap;
}
@keyframes nx-hbar-grow {
  from {
    transform: scaleX(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-hbar-chart__fill {
    animation: none;
  }
  .nx-hbar-chart__row > * {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-hbar-chart__fill {
    background: CanvasText !important;
  }
}
</style>
